import { Controller } from "stimulus"

/**
 * Campaign cover image with locale tabs.
 *
 * Each locale has its own file input, remove checkbox, and preview.
 * Switching tabs shows/hides the corresponding panel and updates the
 * shared preview area with the active locale's image.
 */
export default class extends Controller {
  static targets = [
    "tab",
    "panel",
    "preview",
    "previewImage",
    "emptyState",
    "removeBtn"
  ]

  static values = {
    locale: String
  }

  connect() {
    this.pendingPreviews = {}
    if (!this.localeValue) this.localeValue = "en"
    this.switchTo(this.localeValue)
  }

  // Called from data-action on tab buttons
  selectTab(event) {
    const locale = event.currentTarget.dataset.locale
    this.switchTo(locale)
  }

  switchTo(locale) {
    this.localeValue = locale

    // Toggle tabs
    this.tabTargets.forEach(tab => {
      const active = tab.dataset.locale === locale
      tab.classList.toggle("btn-primary", active)
      tab.classList.toggle("btn-secondary", !active)
    })

    // Update shared preview
    this.refreshPreview(locale)
  }

  // Called when a file input changes
  fileChanged(event) {
    const locale = event.currentTarget.dataset.locale
    const file = event.target.files?.[0]
    if (!file) return

    this.uncheckRemove(locale)

    const reader = new FileReader()
    reader.onload = (e) => {
      this.pendingPreviews[locale] = e.target?.result
      if (locale === this.localeValue) {
        this.showPreview(e.target?.result)
      }
    }
    reader.readAsDataURL(file)
  }

  // Called when remove checkbox is toggled
  removeToggled(event) {
    const locale = event.currentTarget.dataset.locale
    if (event.target.checked) {
      this.clearFile(locale)
      delete this.pendingPreviews[locale]
      if (locale === this.localeValue) {
        this.showEmpty()
      }
    }
  }

  // Called from the X button on the preview
  removeClicked() {
    const locale = this.localeValue
    const panel = this.findPanel(locale)
    const checkbox = panel?.querySelector('[data-campaign-cover-locale-target="removeCheckbox"]')
    if (checkbox) checkbox.checked = true

    this.clearFile(locale)
    delete this.pendingPreviews[locale]
    this.showEmpty()
  }

  // Click the preview area to trigger file input for current locale
  clickUpload() {
    const locale = this.localeValue
    const panel = this.findPanel(locale)
    const input = panel?.querySelector('input[type="file"]')
    if (input) input.click()
  }

  // Drag & drop
  dragEnter(event) { event.preventDefault() }
  dragOver(event) { event.preventDefault() }
  dragLeave(event) { event.preventDefault() }

  drop(event) {
    event.preventDefault()
    const locale = this.localeValue
    const file = event.dataTransfer?.files?.[0]
    if (!file) return

    this.assignFileToInput(locale, file)
    this.uncheckRemove(locale)

    const reader = new FileReader()
    reader.onload = (e) => {
      this.pendingPreviews[locale] = e.target?.result
      this.showPreview(e.target?.result)
    }
    reader.readAsDataURL(file)
  }

  // --- Helpers ---

  findPanel(locale) {
    return this.panelTargets.find(panel => panel.dataset.locale === locale)
  }

  refreshPreview(locale) {
    // Priority: pending local file > existing server image
    if (this.pendingPreviews[locale]) {
      this.showPreview(this.pendingPreviews[locale])
      return
    }

    const panel = this.findPanel(locale)
    const serverUrl = panel?.dataset.serverImageUrl
    if (serverUrl) {
      // Check if remove is checked
      const removeCheckbox = panel?.querySelector('[data-campaign-cover-locale-target="removeCheckbox"]')
      if (removeCheckbox?.checked) {
        this.showEmpty()
      } else {
        this.showPreview(serverUrl)
      }
    } else {
      this.showEmpty()
    }
  }

  showPreview(url) {
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.src = url
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("hidden")
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.remove("hidden")
    }
  }

  showEmpty() {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.add("hidden")
    }
    if (this.hasPreviewImageTarget) {
      this.previewImageTarget.removeAttribute("src")
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden")
    }
    if (this.hasRemoveBtnTarget) {
      this.removeBtnTarget.classList.add("hidden")
    }
  }

  clearFile(locale) {
    const panel = this.findPanel(locale)
    const input = panel?.querySelector('input[type="file"]')
    if (input) {
      input.value = ""
      try {
        input.files = new DataTransfer().files
      } catch (_) { /* ignore */ }
    }
  }

  assignFileToInput(locale, file) {
    const panel = this.findPanel(locale)
    const input = panel?.querySelector('input[type="file"]')
    if (!input) return
    try {
      const dt = new DataTransfer()
      dt.items.add(file)
      input.files = dt.files
    } catch (_) { /* ignore */ }
  }

  uncheckRemove(locale) {
    const panel = this.findPanel(locale)
    const checkbox = panel?.querySelector('[data-campaign-cover-locale-target="removeCheckbox"]')
    if (checkbox) checkbox.checked = false
  }
}
